import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { useI18n, type MsgKey } from "../../i18n";
import { Modal } from "../Modal";
import { Button } from "../ui";
import { GuideStagePanel } from "./GuideStagePanel";
import { GuideTour } from "./GuideTour";
import { SystemGuideMindMap } from "./SystemGuideMindMap";
import { SystemGuideSequence } from "./SystemGuideSequence";
import { GUIDE_NODES, GUIDE_TERMS, type GuideNode, type GuideStage } from "./guideData";

type PageId = "overview" | GuideStage | "terms";

const PAGES: { id: PageId; tabKey: MsgKey }[] = [
  { id: "overview", tabKey: "sysGuideTabOverview" },
  { id: "standards", tabKey: "sysGuideStageStandards" },
  { id: "review", tabKey: "sysGuideStageReview" },
  { id: "operations", tabKey: "sysGuideStageOperations" },
  { id: "terms", tabKey: "sysGuideTabTerms" },
];

type GuideView =
  | { kind: "pages" }
  /** A tour started from a diagram box; `nodes` is the whole walkthrough or one picked box. */
  | { kind: "tour"; nodes: GuideNode[] };

export function SystemGuide({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [view, setView] = useState<GuideView>({ kind: "pages" });
  const [pageIndex, setPageIndex] = useState(0);

  const reachableNodes = GUIDE_NODES.filter(
    (node) => node.to !== null && (!node.perm || can(node.perm)),
  );

  // Reopening the guide should start from the overview rather than wherever it was left.
  useEffect(() => {
    if (open) {
      setView({ kind: "pages" });
      setPageIndex(0);
    }
  }, [open]);

  const close = useCallback(() => {
    setView({ kind: "pages" });
    onOpenChange(false);
  }, [onOpenChange]);

  const pageId: PageId = PAGES[pageIndex]?.id ?? "overview";
  const isTouring = open && view.kind === "tour";
  const startTour = (nodes: GuideNode[]) => setView({ kind: "tour", nodes });

  return (
    <>
      <Modal
        open={open && !isTouring}
        onClose={close}
        size="xl"
        title={t("sysGuideTitle")}
        description={t("sysGuideIntro")}
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((index) => Math.max(0, index - 1))}
              >
                {t("sysGuidePrev")}
              </Button>
              <span className="text-xs text-slate-500">
                {t("sysGuidePageOf", { current: pageIndex + 1, total: PAGES.length })}
              </span>
              <Button
                variant="secondary"
                disabled={pageIndex === PAGES.length - 1}
                onClick={() => setPageIndex((index) => Math.min(PAGES.length - 1, index + 1))}
              >
                {t("sysGuideNext")}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={close}>
                {t("sysGuideClose")}
              </Button>
              <Button
                disabled={reachableNodes.length === 0}
                title={reachableNodes.length === 0 ? t("sysGuideTourEmpty") : undefined}
                onClick={() => startTour(reachableNodes)}
              >
                {t("sysGuideStartTour")}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <div role="tablist" aria-label={t("sysGuideTitle")} className="flex flex-wrap gap-1 border-b border-slate-200">
            {PAGES.map((item, index) => {
              const selected = index === pageIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`-mb-px rounded-t-md border-b-2 px-3 py-2 text-sm font-medium transition ${
                    selected
                      ? "border-brand-600 text-brand-700"
                      : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
                  }`}
                  onClick={() => setPageIndex(index)}
                >
                  {t(item.tabKey)}
                </button>
              );
            })}
          </div>

          {pageId === "overview" ? (
            <div className="space-y-8">
              <section>
                <h3 className="text-sm font-bold text-slate-800">{t("sysGuideMindMapTitle")}</h3>
                <p className="mb-3 mt-0.5 text-sm text-slate-500">{t("sysGuideMindMapDesc")}</p>
                <SystemGuideMindMap onSelect={(node) => startTour([node])} />
              </section>
              <section>
                <h3 className="text-sm font-bold text-slate-800">{t("sysGuideSequenceTitle")}</h3>
                <p className="mb-3 mt-0.5 text-sm text-slate-500">{t("sysGuideSequenceDesc")}</p>
                <SystemGuideSequence />
              </section>
            </div>
          ) : null}

          {pageId === "standards" || pageId === "review" || pageId === "operations" ? (
            <GuideStagePanel stage={pageId} onSelect={(node) => startTour([node])} />
          ) : null}

          {pageId === "terms" ? (
            <section>
              <h3 className="text-sm font-bold text-slate-800">{t("sysGuideTermsTitle")}</h3>
              <dl className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                {GUIDE_TERMS.map((term) => (
                  <div key={term.id}>
                    <dt className="text-sm font-semibold text-slate-700">{t(term.termKey)}</dt>
                    <dd className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      {t(term.descKey)}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}
        </div>
      </Modal>
      {isTouring && view.kind === "tour" ? (
        <GuideTour
          nodes={view.nodes}
          onExit={close}
          onBackToDiagram={() => setView({ kind: "pages" })}
        />
      ) : null}
    </>
  );
}
